if (!customElements.get('sticky-atc-panel')) {
  const SCROLL_BOTTOM_OFFSET = 100;
  const QTY_SYNC_DELAY = 10;

  class StickyAtcPanel extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      this.isStickyCta = this.classList.contains('sticky-cta');
      this.mainButtonIsAboveViewport = false;
      this.initializeElements();

      if ('IntersectionObserver' in window && 'MutationObserver' in window) {
        this.bindEvents();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('scroll', StickyAtcPanel.handleScroll);
      if (this.handleScrollBound) {
        window.removeEventListener('scroll', this.handleScrollBound);
      }

      if (this.handleVariantChangeBound) {
        document.removeEventListener('on:variant:change', this.handleVariantChangeBound);
      }

      if (this.stickyAddBtn && this.handleAddToCartBound) {
        this.stickyAddBtn.removeEventListener('click', this.handleAddToCartBound);
      }

      if (this.stickyQtyInput) {
        if (this.syncStickyToMainBound) {
          this.stickyQtyInput.removeEventListener('change', this.syncStickyToMainBound);
          this.stickyQtyInput.removeEventListener('input', this.syncStickyToMainBound);
        }
      }

      if (this.mainQtyInput) {
        if (this.syncMainToStickyBound) {
          this.mainQtyInput.removeEventListener('change', this.syncMainToStickyBound);
          this.mainQtyInput.removeEventListener('input', this.syncMainToStickyBound);
        }
      }

      if (this.mainQtyButtons) {
        this.mainQtyButtons.forEach(btn => {
          if (this.handleMainQtyButtonClickBound) {
            btn.removeEventListener('click', this.handleMainQtyButtonClickBound);
          }
        });
      }

      if (this.stickyQtyButtons) {
        this.stickyQtyButtons.forEach(btn => {
          if (this.handleStickyQtyButtonClickBound) {
            btn.removeEventListener('click', this.handleStickyQtyButtonClickBound);
          }
        });
      }

      if (this.panelObserver) {
        this.panelObserver.disconnect();
      }
      if (this.ctaObserver) {
        this.ctaObserver.disconnect();
      }
    }

    initializeElements() {
      this.productSection = this.closest('.cc-main-product');
      if (!this.productSection) return;

      this.productInfo = this.productSection.querySelector('.product-info');
      this.productForm = this.productSection.querySelector('product-form');

      if (this.isStickyCta) {
        this.mainAddToCartBtn = this.productForm?.querySelector('[name="add"]');
        this.mainQtyInput = this.productForm?.querySelector('input[name="quantity"]');
        this.stickyAddBtn = this.querySelector('.sticky-cta__add-btn');
        this.stickyQtyInput = this.querySelector('.sticky-cta__qty quantity-input input');

        this.mainQtyButtons = this.productForm?.querySelectorAll('.qty-input__btn');
        this.stickyQtyButtons = this.querySelectorAll('.sticky-cta__qty .qty-input__btn');
      }
    }

    bindEvents() {

      if (this.isStickyCta) {
        if (this.productSection && this.productForm) {
          this.bindStickyCtaEvents();
        }
      } else {
        if (this.productForm || this.productInfo) {
          this.bindStickyPanelEvents();
        }
      }
    }

    bindStickyPanelEvents() {
      this.panelObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.target === this.productForm && !theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.boundingClientRect.bottom > 0);
          } else if (entry.target === this.productInfo && theme.mediaMatches.md) {
            this.classList.toggle('sticky-atc-panel--out', entry.isIntersecting);
          }
        });
      });

      if (this.productForm) this.panelObserver.observe(this.productForm);
      if (this.productInfo) this.panelObserver.observe(this.productInfo);

      window.addEventListener('scroll', StickyAtcPanel.handleScroll);
    }

    bindStickyCtaEvents() {
      this.handleScrollBound = this.updateVisibility.bind(this);
      this.handleVariantChangeBound = this.handleVariantChange.bind(this);
      this.handleAddToCartBound = this.handleAddToCart.bind(this);
      this.syncStickyToMainBound = this.syncStickyToMain.bind(this);
      this.syncMainToStickyBound = this.syncMainToSticky.bind(this);
      this.handleMainQtyButtonClickBound = this.handleMainQtyButtonClick.bind(this);
      this.handleStickyQtyButtonClickBound = this.handleStickyQtyButtonClick.bind(this);

      if (this.mainAddToCartBtn) {
        this.ctaObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.mainButtonIsAboveViewport = !entry.isIntersecting && entry.boundingClientRect.top < 0;
            this.updateVisibility();
          });
        }, {
          threshold: 0,
          rootMargin: '0px'
        });

        this.ctaObserver.observe(this.mainAddToCartBtn);
      }

      if (this.stickyAddBtn) {
        this.stickyAddBtn.addEventListener('click', this.handleAddToCartBound);
      }

      if (this.stickyQtyInput) {
        this.stickyQtyInput.addEventListener('change', this.syncStickyToMainBound);
        this.stickyQtyInput.addEventListener('input', this.syncStickyToMainBound);
      }

      if (this.mainQtyInput) {
        this.mainQtyInput.addEventListener('change', this.syncMainToStickyBound);
        this.mainQtyInput.addEventListener('input', this.syncMainToStickyBound);
      }

      if (this.mainQtyInput && this.stickyQtyInput) {
        this.stickyQtyInput.value = this.mainQtyInput.value;
      }

      if (this.mainQtyButtons) {
        this.mainQtyButtons.forEach(btn => {
          btn.addEventListener('click', this.handleMainQtyButtonClickBound);
        });
      }

      if (this.stickyQtyButtons) {
        this.stickyQtyButtons.forEach(btn => {
          btn.addEventListener('click', this.handleStickyQtyButtonClickBound);
        });
      }

      document.addEventListener('variant:change', this.handleVariantChangeBound);
      window.addEventListener('scroll', this.handleScrollBound);
    }

    updateVisibility() {
      const shouldBeVisible = this.mainButtonIsAboveViewport;
      const isCurrentlyVisible = this.classList.contains('sticky-cta--visible');

      if (shouldBeVisible !== isCurrentlyVisible) {
        this.classList.toggle('sticky-cta--visible', shouldBeVisible);
      }
    }

    handleAddToCart(e) {
      e.preventDefault();

      const productFormId = this.stickyAddBtn?.dataset?.productFormId;
      if (!productFormId) return;

      const mainForm = document.getElementById(productFormId);
      if (!mainForm) return;

      this.syncStickyToMain();

      const submitBtn = mainForm.querySelector('[name="add"]');
      if (submitBtn && !submitBtn.disabled) {
        submitBtn.click();
      }
    }

    syncStickyToMain() {
      if (this.mainQtyInput && this.stickyQtyInput && this.mainQtyInput.value !== this.stickyQtyInput.value) {
        this.mainQtyInput.value = this.stickyQtyInput.value;
        this.mainQtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    syncMainToSticky() {
      if (this.mainQtyInput && this.stickyQtyInput && this.stickyQtyInput.value !== this.mainQtyInput.value) {
        this.stickyQtyInput.value = this.mainQtyInput.value;
      }
    }

    handleVariantChange(e) {
      if (!e.detail) return;

      const { variant, sectionId } = e.detail;
      const thisSectionId = this.dataset.sectionId;

      if (sectionId !== thisSectionId) return;

      if (this.stickyAddBtn) {
        if (!variant) {
          this.stickyAddBtn.disabled = true;
          this.stickyAddBtn.textContent = this.stickyAddBtn.dataset.notSelectedText || 'Select option';
        } else if (!variant.available) {
          this.stickyAddBtn.disabled = true;
          this.stickyAddBtn.textContent = this.stickyAddBtn.dataset.soldOutText || 'Sold out';
        } else {
          this.stickyAddBtn.disabled = false;
          this.stickyAddBtn.textContent = this.stickyAddBtn.dataset.addToCartText || 'Add to cart';
        }
      }

      const priceContainer = this.querySelector('.sticky-cta__price');
      if (priceContainer && variant) {
        const mainPriceContainer = this.productSection?.querySelector('.product-info__price .price');
        if (mainPriceContainer) {
          const newPrice = mainPriceContainer.cloneNode(true);
          priceContainer.innerHTML = '';
          priceContainer.appendChild(newPrice);
        }
      }
    }

    handleMainQtyButtonClick() {
      setTimeout(() => this.syncMainToSticky(), QTY_SYNC_DELAY);
    }

    handleStickyQtyButtonClick() {
      setTimeout(() => this.syncStickyToMain(), QTY_SYNC_DELAY);
    }

    /**
     * Watches for a scroll to the bottom of the page
     */
    static handleScroll() {
      document.body.classList.toggle(
        'scrolled-to-bottom',
        window.scrollY + window.innerHeight + SCROLL_BOTTOM_OFFSET > document.body.scrollHeight
      );
    }
  }

  customElements.define('sticky-atc-panel', StickyAtcPanel);
}
